import React from 'react';

import uniq from 'lodash/uniq';

import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import ReadOnlyField from '~/components/Offers/Detail/OfferBuilder/shared/ReadOnlyField';
import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { usePropertySetupContext } from '../../PricingToolSessionContext';

import { LOCATION_FIELDS, type LocationField, deriveLocationOptions } from './propertySetupModel';

const LOCATION_LABELS: Record<LocationField, string> = {
  dealCategory: 'Deal category',
  offerCountry: 'Offer country',
  destination: 'Destination',
  subDestination: 'Sub-destination',
};

const DATE_FIELDS: Array<{ key: 'travelFrom' | 'travelTo'; label: string }> = [
  { key: 'travelFrom', label: 'Travel from' },
  { key: 'travelTo', label: 'Travel to' },
];

function toNumberOrNull(raw: string): number | null {
  if (raw === '') {
    return null;
  }

  const next = Number(raw);
  return Number.isFinite(next) ? next : null;
}

export default function PropertySetupTab() {
  const { values, setField, setCurrency, setLocation, currencyOptions, autoResolveFxRate } = usePropertySetupContext();

  if (!values) {
    return (
      <TabPanelShell subtitle="Loading opportunity details…" title="Property Setup">
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </TabPanelShell>
    );
  }

  const locationOptions = deriveLocationOptions(values);

  return (
    <TabPanelShell subtitle="Pre-filled from the account and opportunity where available." title="Property Setup">
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          }}
        >
          <ReadOnlyField field={{ label: 'Property name', value: values.propertyName ?? '' }} />
          {LOCATION_FIELDS.map((field) => (
            <TextField
              key={field}
              fullWidth
              select
              InputLabelProps={{ shrink: true }}
              disabled={locationOptions[field].disabled}
              label={LOCATION_LABELS[field]}
              value={values[field] ?? ''}
              onChange={(event) => setLocation(field, event.target.value || null)}
            >
              <MenuItem value="">None</MenuItem>
              {locationOptions[field].options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          ))}
          {DATE_FIELDS.map((field) => (
            <TextField
              key={field.key}
              fullWidth
              InputLabelProps={{ shrink: true }}
              label={field.label}
              type="date"
              value={values[field.key] ?? ''}
              onChange={(event) => setField(field.key, event.target.value || null)}
            />
          ))}
          <TextField
            fullWidth
            select
            InputLabelProps={{ shrink: true }}
            label="Currency"
            value={values.currency ?? ''}
            onChange={(event) => setCurrency(event.target.value || null)}
          >
            {/* Keeps a draft's saved currency selectable even if FX rates no longer list it. */}
            {uniq([...currencyOptions, ...(values.currency ? [values.currency] : [])]).map((currency) => (
              <MenuItem key={currency} value={currency}>
                {currency}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Stack alignItems="flex-end" direction="row" spacing={1} sx={{ maxWidth: 240 }}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="FX rate (AUD → currency)"
            type="number"
            value={values.fxRate ?? ''}
            onChange={(event) => setField('fxRate', toNumberOrNull(event.target.value))}
          />
          <Button color="secondary" size="small" variant="outlined" onClick={autoResolveFxRate}>
            Auto
          </Button>
        </Stack>

        <Stack alignItems="flex-end" direction="row" spacing={1} sx={{ maxWidth: 240 }}>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="PRV required lead price"
            type="number"
            value={values.prvReqLeadPrice ?? ''}
            onChange={(event) => setField('prvReqLeadPrice', toNumberOrNull(event.target.value))}
          />
        </Stack>
      </Stack>
    </TabPanelShell>
  );
}
